"use client";
import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import "../AutoVerkaufen.css";

import BildTextInfo from "@/components/BildTextInfo/BildTextInfo";
import { TopSectionAutoVerkaufen } from "../sections/TopSection";
import { AutoVerkaufenGrid } from "@/common/AutoVerkaufenGrid";
import { Banner } from "@/common/Banner";
import sellCarCash from "@/assets/auto-verkaufen/Rückbank-autoverkauf.avif";
import höchstenPreis from "@/assets/auto-verkaufen/wir-kaufen-dein-auto-verkauf.avif";
import vonZuhauseAus from "@/assets/auto-verkaufen/Verkauft-handy.avif";
import { fragenTirol } from "../data";
import { HäufigeFragen } from "@/common/HäufigeFragen";
import FAQImage from "@/assets/faq-autoverkauf-autoeinfachlos.webp";

// Only needed after step 11; keep auth-related code/CSS out of initial paint.
// Below-the-fold section; defer to reduce initial route JS/CSS.
const Ratgeber = dynamic(() => import("@/components/Ratgeber/Ratgeber"), {
  ssr: false,
});

const AnmeldenSection = dynamic(() => import("@/app/anmelden/AnmeldenStepRedirect"), {
  ssr: false,
});

function Tirol() {
  const [step, setStep] = useState(1);
  // selected values
  const [selectedBrand, setSelectedBrand] = useState({ id: "", name: "" });
  const [selectedModel, setSelectedModel] = useState({ id: "", name: "" });
  const [selectedYear, setSelectedYear] = useState({ id: "", name: "" });
  const [selectedBauform, setSelectedBauform] = useState({ id: "", name: "" });
  const [selectedFuel, setSelectedFuel] = useState({ id: "", name: "" });
  const [selectedVariant, setSelectedVariant] = useState({ id: "", name: "" });
  const [selectedPs, setSelectedPs] = useState({ id: "", name: "" });
  const [selectedTransmission, setSelectedTransmission] = useState({
    id: "",
    name: "",
  });
  const [selectedDoors, setSelectedDoors] = useState({ id: "", name: "" });
  const [selectedModelType, setSelectedModelType] = useState({
    id: "",
    name: "",
  });
  const [selectedKmStand, setSelectedKmStand] = useState("");
  const isSecondaryMethod = true;

  useEffect(() => {
    if (step >= 10) {
      if (typeof AnmeldenSection?.preload === "function") {
        AnmeldenSection.preload();
      } else {
        import("@/app/anmelden/AnmeldenStepRedirect");
      }
    }
  }, [step]);


  return (
    <div className="home-container">
      <TopSectionAutoVerkaufen
        title={<>Auto in Tirol verkaufen: So erzielst du den besten Preis</>}
        text={
          <>
            84% der Autoeinfachlos-Kunden bekommen mehr Geld.*
            <br className="desktop" />
            Hol dir deine kostenlose und sofortige Bewertung für Tirol.
          </>
        }
        buttonTitle="Bewertung ansehen"
        linkname="Tirol"
        link="/auto-verkaufen/tirol"
        customNav="Auto verkaufen"
        customNavLink="/auto-verkaufen"
        step={step}
        setStep={setStep}
        selectedBrand={selectedBrand}
        setSelectedBrand={setSelectedBrand}
        selectedModel={selectedModel}
        setSelectedModel={setSelectedModel}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        selectedBauform={selectedBauform}
        setSelectedBauform={setSelectedBauform}
        selectedFuel={selectedFuel}
        setSelectedFuel={setSelectedFuel}
        selectedVariant={selectedVariant}
        setSelectedVariant={setSelectedVariant}
        selectedPs={selectedPs}
        setSelectedPs={setSelectedPs}
        selectedTransmission={selectedTransmission}
        setSelectedTransmission={setSelectedTransmission}
        selectedDoors={selectedDoors}
        setSelectedDoors={setSelectedDoors}
        setSelectedModelType={setSelectedModelType}
        selectedModelType={selectedModelType}
        selectedKmStand={selectedKmStand}
        setSelectedKmStand={setSelectedKmStand}
      />

      {step === 12 && (
        <>
          <AnmeldenSection
            title="Bitte gib deine E-Mail-Adresse ein, um fortzufahren"
            fin={null}
            isManual={isSecondaryMethod}
            marke={selectedBrand}
            model={selectedModel}
            baujahr={selectedYear}
            bodytype={selectedBauform}
            fueltype={selectedFuel}
            variant={selectedVariant}
            ps={selectedPs}
            gearbox={selectedTransmission}
            doors={selectedDoors}
            modelType={selectedModelType}
            kmStand={selectedKmStand}
            page="/auto-verkaufen/tirol"
          />
          <Banner removeContent className="Banner-anmelden" />
        </>
      )}

      {step !== 12 && (
        <div className="Page background-color AV-below-fold">
          <div className="Page-wrapper">
            <AutoVerkaufenGrid
              image={sellCarCash}
              leftTitle="Dein Auto in Tirol verkaufen – warum es oft komplizierter ist, als man denkt"
              leftText={
                <>
                  <p>
                    Wenn du dein Auto in Tirol verkaufen möchtest, stehst du vor
                    einigen besonderen Herausforderungen. Die Nachfrage nach
                    Gebrauchtwagen ist hoch, da viele Menschen in Tirol auf das
                    Auto angewiesen sind. Besonders gefragt sind kompakte
                    Fahrzeuge für den Alltag, aber auch robuste Modelle für die
                    Berge und SUVs für Familien. Gleichzeitig kann der
                    Verkaufsprozess mühsam sein. Viele Fahrzeuge haben durch
                    häufige Bergfahrten oder Wintereinsätze einen stärkeren
                    Verschleiß. Dazu kommen Preisverhandlungen, Inserate und das
                    Risiko unseriöser Angebote.
                  </p>
                  <p>
                    Mit Autoeinfachlos wird der Autoverkauf in Tirol für dich
                    schnell, fair und unkompliziert.
                  </p>
                </>
              }
              rightFirstTitle="Auto verkaufen in Tirol – worauf kommt es an?"
              rightFirstText={
                <>
                  <p>
                    Der Tiroler Markt für Gebrauchtwagen hat seine Eigenheiten.
                    Fahrzeuge mit Allradantrieb sind hier deutlich gefragter als
                    in anderen Regionen. Ebenso beliebt sind Klein- und
                    Mittelklassewagen, die für Pendler in Innsbruck oder im
                    Unterland praktisch sind.
                  </p>

                  <p>
                    <strong>Typische Probleme beim Auto verkaufen in Tirol:</strong>
                  </p>
                  <ul className="custom-unordered-list">
                    <li>zeitaufwendige Inserate und viele Anfragen</li>
                    <li>Unsicherheit bei der Preisermittlung</li>
                    <li>unzuverlässige Käufer und Risiko von Zahlungsausfällen</li>
                  </ul>

                  <p>
                    Mit Autoeinfachlos musst du dich darum nicht kümmern. Wir
                    übernehmen die Arbeit und bringen dein Auto direkt in unser
                    Händlernetzwerk.
                  </p>

                  <h3>Dein Vorteil mit Autoeinfachlos in Tirol</h3>
                  <p>Mit Autoeinfachlos verkaufst du dein Auto in Tirol einfach und sicher:</p>
                  <ul className="custom-unordered-list">
                    <li>
                      Zugriff auf ein Händlernetzwerk mit über 5.000 Partnern,
                      auch in Tirol
                    </li>
                    <li>
                      Auktion mit mehreren Bietern, so erzielst du automatisch
                      den besten Preis
                    </li>
                    <li>
                      Abholung direkt bei dir, egal ob in Innsbruck, Kufstein
                      oder im Oberland
                    </li>
                    <li>schnelle und sichere Zahlung ohne Risiko</li>
                  </ul>

                  <p>Damit sparst du Zeit und erhältst einen fairen Marktpreis.</p>
                </>
              }
              rightSecondTitle="Autoankauf in Tirol – privat oder Händler"
              rightSecondText={
                <>
                  <p>
                    Ein Privatverkauf klingt oft verlockend. Doch beim Auto
                    verkaufen privat in Tirol treten schnell Nachteile auf:
                  </p>
                  <ul className="custom-unordered-list secondary">
                    <li>viele Termine und Probefahrten, oft ohne Ergebnis</li>
                    <li>unzuverlässige Käufer und Preisdrückerei</li>
                    <li>hoher Zeitaufwand für Inserate</li>
                  </ul>

                  <p>Der Autoankauf über unser Händlernetzwerk ist die bessere Lösung:</p>
                  <ul className="custom-unordered-list secondary">
                    <li>geprüfte Käufer</li>
                    <li>seriöse Abwicklung</li>
                    <li>schnelle Zahlung</li>
                  </ul>

                  <p>
                    Mit Autoeinfachlos hast du den Vorteil, dass du die
                    Reichweite vieler Händler mit der Sicherheit einer
                    professionellen Abwicklung kombinierst.
                  </p>
                </>
              }
            />

            <div className="Banner-sections">
              <BildTextInfo
                image={höchstenPreis}
                h2
                isBig
                fontSize="36px"
                title="Was ist dein Auto in Tirol wert?"
                alt="Auto in Tirol bewerten"
                text={
                  <>
                    <p>
                      Die Bewertung deines Autos hängt in Tirol von
                      verschiedenen Faktoren ab:
                    </p>
                    <ul className="custom-unordered-list">
                      <li>Marke und Modell</li>
                      <li>Baujahr und Kilometerstand</li>
                      <li>
                        allgemeiner Zustand, auch nach Wintersaisonen oder
                        Bergfahrten
                      </li>
                      <li>Nachfrage nach Allradfahrzeugen und SUVs</li>
                    </ul>

                    <p>
                      Bei Autoeinfachlos kannst du dein Auto kostenlos bewerten
                      lassen. Du erhältst in wenigen Sekunden eine Einschätzung
                      und startest direkt in die Auktion.
                    </p>
                    <p>
                      👉{" "}
                      <Link className="link" href="/auto-bewerten">
                        Jetzt Auto bewerten
                      </Link>
                    </p>

                    <h3>So funktioniert der Verkauf bei Autoeinfachlos in Tirol</h3>
                    <p>Der Ablauf ist einfach und bequem:</p>
                    <ul className="custom-unordered-list">
                      <li>Auto online bewerten lassen</li>
                      <li>Auktion im Händlernetzwerk starten</li>
                      <li>Höchstgebot erhalten</li>
                      <li>Abholung direkt bei dir in Tirol</li>
                      <li>sichere Bezahlung</li>
                    </ul>
                    <p>So sparst du dir Zeit, Aufwand und Nerven.</p>
                  </>
                }
              />
            </div>

            <HäufigeFragen
              title="Häufige Fragen rund um den Autoverkauf in Tirol"
              fragen={fragenTirol}
              image={FAQImage}
              removePaddingBottom
            />

            <div style={{ paddingTop: "120px" }}>
              <Ratgeber
                title="Mehr zum Thema Auto verkaufen in Tirol"
                autoVerkaufen
                autoBewerten
                listenpreisBerechnen
                fahrgestellnummerPruefen
                kaputtesAuto
                wasistmeinautowert
              />
            </div>

            <div className="Banner-sections-marken">
              <BildTextInfo
                image={vonZuhauseAus}
                h2
                isBig
                fontSize="36px"
                title="Auto verkaufen in Tirol leicht gemacht"
                alt="Auto verkaufen in Tirol leicht gemacht"
                text={
                  <>
                    <p>
                      Wenn du dein Auto in Tirol verkaufen möchtest, musst du
                      dich nicht mit Inseraten, unsicheren Käufern oder
                      komplizierten Preisverhandlungen beschäftigen. Mit
                      Autoeinfachlos nutzt du ein großes Händlernetzwerk, eine
                      regionale Abwicklung und ein faires Auktionsverfahren.
                    </p>
                    <p>
                      <Link className="link" href="/auto-bewerten">
                        Jetzt Auto kostenlos bewerten und verkaufen
                      </Link>
                    </p>
                  </>
                }
              />
            </div>
          </div>
          <Banner
            link="/auto-bewerten"
            title="Jetzt Auto kostenlos bewerten und verkaufen"
          />
        </div>
      )}
    </div>
  );
}

export default Tirol;
